/**
 * Finance Phase 2 (2026-10-28): the financial reports — their words in English
 * and Arabic, spread into the one dictionary in i18n.tsx so a missing key is
 * still a compile error. Kept in a file of their own, as Phase 1's are.
 */

export const reportsEn = {
  // Navigation
  'nav.groupReports': 'Financial reports',
  'nav.frOverview': 'Financial overview',
  'nav.frPnl': 'Profit & loss',
  'nav.frBalanceSheet': 'Balance sheet',
  'nav.frCashFlow': 'Cash flow',
  'nav.frPartners': 'Partners statement',
  'nav.frMonthly': 'Monthly summary',
  'nav.frReconciliation': 'Reconciliation',

  // Controls
  'fr.period': 'Period',
  'fr.period.thisMonth': 'This month',
  'fr.period.lastMonth': 'Last month',
  'fr.period.ytd': 'Year to date',
  'fr.period.thisYear': 'This year',
  'fr.period.lastYear': 'Last year',
  'fr.period.custom': 'Custom',
  'fr.from': 'From',
  'fr.to': 'To',
  'fr.asOf': 'As of',
  'fr.export': 'Export CSV',
  'fr.exported': 'The file is downloading.',

  // What every report says about itself
  'fr.inBase': 'In {currency}',
  'fr.generated': 'Worked out {time}',
  'fr.cutoverOn': 'Accounting cutover {date}',
  'fr.cutoverDraft': 'Accounting cutover {date} — still a draft',
  'fr.cutoverNone': 'No accounting cutover yet',
  'fr.basis': 'Revenue when received · expenses when approved · each paid payroll once · historical months on their last day',
  'fr.calendarYear': 'Calendar years',
  'fr.readOnly': 'Reports only read: nothing here records, changes or reverses money.',
  'fr.status.VERIFIED': 'Verified',
  'fr.status.PREVIEW': 'Preview — not yet reconciled',
  'fr.status.UNVERIFIED': 'Recorded, not verified',
  'fr.status.PARTIAL': 'Partial',
  'fr.status.NOT_RECONCILED': 'Not reconciled',
  'fr.status.SETUP_NEEDED': 'Setup needed',
  'fr.status.UNAVAILABLE': 'Unavailable',
  'fr.status.RESTRICTED': 'Needs access',
  'fr.statusHint.VERIFIED': 'On a confirmed opening position, and its checks pass.',
  'fr.statusHint.PREVIEW': 'The cutover is still a draft: the opening position may change.',
  'fr.statusHint.UNVERIFIED': 'There is no opening position: only what was recorded is counted.',
  'fr.statusHint.PARTIAL': 'Part of the period is before the cutover, or some of it was never provided.',
  'fr.statusHint.NOT_RECONCILED': 'A check failed. The difference is shown, never absorbed.',
  'fr.statusHint.SETUP_NEEDED': 'Set the accounting cutover and the opening position in Financial setup first.',
  'fr.statusHint.UNAVAILABLE': 'The day is before the accounting cutover: nothing reliable is known about it.',
  'fr.statusHint.RESTRICTED': 'Your role does not cover it.',
  'fr.setupLink': 'Open Financial setup',

  // Notices
  'fr.notice.NO_CONFIRMED_CUTOVER': 'There is no confirmed accounting cutover, so no month is known to be complete.',
  'fr.notice.PERIOD_BEFORE_CUTOVER': 'Part of this period is before the accounting cutover ({cutoverDate}): revenue and other costs from then may be incomplete.',
  'fr.notice.HISTORICAL_RESTRICTED': 'Your role does not read the historical months, so their costs are left out of these totals.',
  'fr.notice.MONTHS_NOT_PROVIDED': 'Some months have nothing provided — unknown, not zero.',
  'fr.notice.OVERLAP_CHANGED': 'Live records changed in a historical month after it was reviewed: it may count some costs twice.',
  'fr.notice.UNCONVERTED': 'Money in {currencies} has no rate today and is left out of the totals in the base currency — never counted as zero.',
  'fr.notice.EXPENSES_AT_TODAY_RATE': 'Expenses in another currency carry no rate of their own: they are valued at today’s rate from Settings › Currencies.',
  'fr.notice.REVENUE_INCLUDES_TAX': 'Paid invoices carrying tax: {invoices}. Revenue is what was collected, tax included — the system keeps no tax account.',
  'fr.notice.PARTIAL_MONTH': 'The period stops inside a month: monthly totals — a historical month, a payroll with no expense — count on its last day, so not here.',
  'fr.notice.NO_CUTOVER': 'Set the accounting cutover and the opening position in Financial setup to read balances.',
  'fr.notice.BEFORE_CUTOVER': 'Before the accounting cutover ({cutoverDate}) the books hold no verified balances.',
  'fr.notice.CUTOVER_DRAFT': 'The accounting cutover is a draft: this is a preview.',
  'fr.notice.NOT_BALANCED': 'Assets do not equal liabilities plus equity: the difference is shown below.',
  'fr.notice.HISTORICAL_REACHES_CUTOVER': 'A historical month reaches the cutover: its cost is in the P&L, but its money is not in these books.',
  'fr.notice.PER_CURRENCY': 'Each currency stands on its own: amounts in two currencies are never added together.',
  'fr.notice.STARTS_AT_CUTOVER': 'Starts at the accounting cutover ({cutoverDate}): before it the books do not hold the money that moved.',
  'fr.notice.ENDS_TODAY': 'Ends today ({today}).',
  'fr.notice.CASH_NOT_RECONCILED': 'The statement does not agree with the books’ own total: the difference is shown.',
  'fr.notice.NO_BANK_STATEMENTS': 'The system holds no bank statements: comparing the ending cash with the bank’s own is a step done by hand.',
  'fr.notice.NO_OFFSET': 'A partner’s loan and advances are two balances: the net is for information only, and nothing is offset without an approved settlement.',
  'fr.notice.OWNERSHIP_NOT_PROFIT': 'Ownership shares share nothing out by themselves: profit is paid only by a recorded distribution.',
  'fr.notice.ROWS_HIDDEN': 'Some rows are not shown: your role does not read them.',

  // Figures
  'fr.loss': 'Loss',
  'fr.profit': 'Profit',
  'fr.leftOut': 'Leaves out {amounts} with no rate',
  'fr.atTodayRate': 'Includes amounts valued at today’s rate',
  'fr.notProvided': 'Not provided',
  'fr.noRecords': 'No records',
  'fr.open': 'Open',
  'fr.total': 'Total',
  'fr.month': 'Month',
  'fr.currency': 'Currency',
  'fr.amount': 'Amount',
  'fr.byCurrency': 'In the currencies it was recorded in',

  // Dashboard
  'fr.card.revenue': 'Total revenue',
  'fr.card.salaries': 'Total salaries',
  'fr.card.otherExpenses': 'Other operating expenses',
  'fr.card.operatingExpenses': 'Total operating expenses',
  'fr.card.netProfit': 'Net profit / loss',
  'fr.card.cash': 'Cash & bank balance',
  'fr.card.partnerLoans': 'Partner loans payable',
  'fr.card.partnerAdvances': 'Partner advances receivable',
  'fr.card.asOf': 'As of {date}',
  'fr.card.period': '{from} – {to}',
  'fr.card.none': 'None',
  'fr.chart.revenueVsExpenses': 'Revenue and operating expenses',
  'fr.chart.profitTrend': 'Profit or loss, month by month',
  'fr.chart.breakdown': 'Operating expenses, by where they come from',
  'fr.chart.cashMonthly': 'Cash in and out, from the cutover on',
  'fr.chart.partners': 'Partner loans and advances',
  'fr.series.revenue': 'Revenue',
  'fr.series.expenses': 'Operating expenses',
  'fr.series.profit': 'Profit / loss',
  'fr.series.inflow': 'In',
  'fr.series.outflow': 'Out',
  'fr.series.loans': 'Loans payable',
  'fr.series.advances': 'Advances receivable',
  'fr.series.salariesCurrent': 'Salaries — current',
  'fr.series.salariesHistorical': 'Salaries — historical',
  'fr.series.otherCurrent': 'Other expenses — current',
  'fr.series.otherHistorical': 'Other expenses — historical',
  'fr.coverageSummary': 'What these months rest on',
  'fr.coverageSummary.notProvided': 'Months with something not provided: {n}',
  'fr.coverageSummary.overlapChanged': 'Months not reconciled: {n}',
  'fr.coverageSummary.missingDocuments': 'Historical records with no file: {n}',
  'fr.coverageSummary.drafts': 'Historical drafts, not counted: {n}',
  'fr.coverageSummary.allClear': 'Every month is covered.',
  'fr.cashChartNone': 'Nothing to show before the accounting cutover.',

  // Coverage
  'fr.coverage.LIVE': 'Recorded',
  'fr.coverage.HISTORICAL': 'Historical summary',
  'fr.coverage.HISTORICAL_AND_LIVE': 'Historical + recorded',
  'fr.coverage.OVERLAP_CHANGED': 'Not reconciled',
  'fr.coverage.NO_RECORDS': 'No records',
  'fr.coverage.NOT_PROVIDED': 'Not provided',
  'fr.coverage.RECORDED': 'Recorded',
  'fr.coverage.profit.COMPLETE': 'Complete',
  'fr.coverage.profit.PARTIAL': 'Partial',
  'fr.coverage.profit.NOT_RECONCILED': 'Not reconciled',
  'fr.coverage.partialMonth': 'Part of the month',
  'fr.coverage.beforeCutover': 'Before the accounting cutover',
  'fr.coverage.files': 'Files: {n}',
  'fr.coverage.noFile': 'No file',
  'fr.coverage.drafts': 'Drafts not counted: {n}',
  'fr.coverage.salaries': 'Salaries',
  'fr.coverage.other': 'Other expenses',
  'fr.coverage.revenue': 'Revenue',

  // Profit and loss
  'fr.pnl.subtitle': 'What was earned and what it cost, for a period — revenue when received, each cost once.',
  'fr.pnl.revenue': 'Revenue',
  'fr.pnl.customerPayments': 'Customer payments received',
  'fr.pnl.refunds': 'Refunds paid',
  'fr.pnl.netRevenue': 'Net revenue',
  'fr.pnl.operatingExpenses': 'Operating expenses',
  'fr.pnl.salariesCurrent': 'Salaries — current',
  'fr.pnl.salariesHistorical': 'Salaries — historical summary',
  'fr.pnl.otherCurrent': 'Other expenses — current',
  'fr.pnl.otherHistorical': 'Other expenses — historical summary',
  'fr.pnl.totalExpenses': 'Total operating expenses',
  'fr.pnl.operatingProfit': 'Operating profit / loss',
  'fr.pnl.nonOperating': 'Other income and expenses',
  'fr.pnl.taxes': 'Income tax',
  'fr.pnl.notRecorded': 'None recorded by the system',
  'fr.pnl.netProfit': 'Net profit / loss',
  'fr.pnl.line.SALARY_EXPENSES': 'Salary expenses, payroll’s included',
  'fr.pnl.line.PAYROLL_WITHOUT_EXPENSE': 'Paid payroll with no expense of its own',
  'fr.pnl.line.POINTS_CASH': 'Points cash paid by hand',
  'fr.pnl.uncategorized': 'No category',
  'fr.pnl.showMonths': 'Month by month',
  'fr.pnl.drill': 'Open the rows behind it',
  'fr.pnl.coverageRow': 'Rests on',
  'fr.pnl.noFinancing': 'Partner loans, capital, advances and distributions are never revenue or expenses: they are in the balance sheet, the cash flow and the partners statement.',

  // Balance sheet
  'fr.bs.subtitle': 'What the company holds, owes and is worth on a day — one currency at a time.',
  'fr.bs.assets': 'Assets',
  'fr.bs.cash': 'Cash and bank',
  'fr.bs.advances': 'Partner advances receivable',
  'fr.bs.prepaid': 'Expenses paid ahead of their date',
  'fr.bs.totalAssets': 'Total assets',
  'fr.bs.liabilities': 'Liabilities',
  'fr.bs.loans': 'Partner loans payable',
  'fr.bs.unpaid': 'Approved expenses not yet paid',
  'fr.bs.totalLiabilities': 'Total liabilities',
  'fr.bs.equity': 'Equity',
  'fr.bs.capital': 'Partner capital',
  'fr.bs.openingEquity': 'Opening equity beyond the partners’ capital (derived at the cutover)',
  'fr.bs.netIncome': 'Net income since the cutover',
  'fr.bs.distributions': 'Profit distributions',
  'fr.bs.adjustments': 'Corrections to partner balances — their other side',
  'fr.bs.totalEquity': 'Total equity',
  'fr.bs.check': 'Assets = liabilities + equity',
  'fr.bs.balanced': 'Balanced',
  'fr.bs.difference': 'Difference: {amount}',
  'fr.bs.derivation': 'How the opening equity was derived',
  'fr.bs.derivationText': 'On the morning of the cutover: {cash} cash + {advances} advances + {prepaid} paid ahead − {loans} partner loans − {unpaid} unpaid expenses − {capital} partner capital.',
  'fr.bs.noReceivables': 'No accounts receivable: revenue counts when it is received, so an unpaid invoice is not an asset in these books. Outstanding invoices are in Reports › Receivables.',
  'fr.bs.historical': 'Historical months that reach the cutover',
  'fr.bs.historicalHint': 'Their cost is in the P&L; their money is not in these books.',

  // Cash flow
  'fr.cf.subtitle': 'Company money in and out, from the accounting cutover on — one currency at a time.',
  'fr.cf.beginning': 'Beginning cash',
  'fr.cf.operating': 'Operating activities',
  'fr.cf.investing': 'Investing activities',
  'fr.cf.investingNone': 'Not tracked by the system',
  'fr.cf.financing': 'Financing activities',
  'fr.cf.net': 'Net',
  'fr.cf.netChange': 'Net change',
  'fr.cf.ending': 'Ending cash',
  'fr.cf.books': 'The books’ own total',
  'fr.cf.reconciled': 'Agrees with the books',
  'fr.cf.difference': 'Difference: {amount}',
  'fr.cf.transfers': 'Transfers between company accounts, left out of the total: {amount}',
  'fr.cf.accounts': 'By account',
  'fr.cf.account': 'Account',
  'fr.cf.unassigned': 'Not assigned to an account',
  'fr.cf.unassignedHint': 'Customer payments, expenses, payroll and points cash name no account in their modules.',
  'fr.cf.in': 'In',
  'fr.cf.out': 'Out',
  'fr.cf.line.CUSTOMER_RECEIPTS': 'Customer payments received',
  'fr.cf.line.REFUNDS_PAID': 'Refunds paid',
  'fr.cf.line.SALARIES_PAID': 'Salaries paid',
  'fr.cf.line.POINTS_CASH_PAID': 'Points cash paid by hand',
  'fr.cf.line.OTHER_EXPENSES_PAID': 'Other expenses paid',
  'fr.cf.line.PARTNER_LOANS_RECEIVED': 'Loans from partners',
  'fr.cf.line.PARTNER_LOANS_REPAID': 'Loans repaid to partners',
  'fr.cf.line.CAPITAL_CONTRIBUTED': 'Capital put in by partners',
  'fr.cf.line.DISTRIBUTIONS_PAID': 'Profit paid out to partners',
  'fr.cf.line.ADVANCES_PAID': 'Advances paid to partners',
  'fr.cf.line.ADVANCES_REPAID': 'Advances returned by partners',

  // Partners
  'fr.partners.subtitle': 'What the company owes each partner, and what each owes it — at the start and the end of a period.',
  'fr.partners.share': 'Share',
  'fr.partners.since': 'since {date}',
  'fr.partners.noShare': 'No share on record',
  'fr.partners.loan': 'Loan payable',
  'fr.partners.advances': 'Advances receivable',
  'fr.partners.capital': 'Capital',
  'fr.partners.distributions': 'Distributions',
  'fr.partners.net': 'Net, for information',
  'fr.partners.beginning': 'At the start',
  'fr.partners.closing': 'At the end',
  'fr.partners.movements': 'In the period',
  'fr.partners.mv.loanReceived': 'Lent',
  'fr.partners.mv.loanRepaid': 'Repaid',
  'fr.partners.mv.expensesPaidAsLoan': 'Expenses paid, as a loan',
  'fr.partners.mv.loanAdjustments': 'Corrections',
  'fr.partners.mv.advancesPaid': 'Advanced',
  'fr.partners.mv.advancesReturned': 'Returned',
  'fr.partners.mv.advanceAdjustments': 'Corrections',
  'fr.partners.mv.capitalContributed': 'Put in',
  'fr.partners.mv.expensesPaidAsCapital': 'Expenses paid, as capital',
  'fr.partners.mv.capitalAdjustments': 'Corrections',
  'fr.partners.mv.distributions': 'Paid out',
  'fr.partners.reconciles': 'Start + movements = end',
  'fr.partners.notReconciled': 'Start + movements ≠ end',
  'fr.partners.totals': 'All partners',
  'fr.partners.none': 'No partners yet.',
  'fr.partners.statement': 'Statement',
  'fr.partners.search': 'Search the statement',
  'fr.partners.history': 'Before the cutover — history',
  'fr.partners.reversed': 'Reversed',
  'fr.partners.noCash': 'No company cash',
  'fr.partners.after': 'Balances after',
  'fr.partners.shareHistory': 'Ownership through time',
  'fr.partners.until': 'until {date}',
  'fr.partners.expensePaid': 'Paid the expense “{description}”',
  'fr.partners.noRows': 'Nothing in this period.',
  'fr.partners.openRecord': 'Open the transaction',

  // Monthly summary
  'fr.monthly.subtitle': 'One row a month — revenue, salaries and other expenses from the live modules and from the historical summaries, the profit, and what each rests on.',
  'fr.monthly.revenue': 'Revenue',
  'fr.monthly.histSalaries': 'Historical salaries',
  'fr.monthly.curSalaries': 'Current salaries',
  'fr.monthly.histOther': 'Historical other expenses',
  'fr.monthly.curOther': 'Current other expenses',
  'fr.monthly.totalExpenses': 'Total expenses',
  'fr.monthly.profit': 'Profit / loss',
  'fr.monthly.files': 'Files',
  'fr.monthly.coverage': 'Coverage',
  'fr.monthly.year': 'Year {year}',
  'fr.monthly.overlaps': 'Historical months whose live records changed after their review',
  'fr.monthly.overlapLine': '{kind} {currency}: reviewed {reviewed}, now {now}',
  'fr.monthly.sources': 'Reconciliation / source coverage',
  'fr.monthly.sourceLive': 'Current: the live modules — payments, approved expenses, paid payroll, points cash paid by hand.',
  'fr.monthly.sourceHistorical': 'Historical: finalized monthly summaries, each with its Excel or PDF file. A draft is never counted.',
  'fr.monthly.sourceOverlap': 'Where a month has both, the historical total was reviewed as leaving the live records out (Phase 1’s overlap review).',
  'fr.monthly.kind.PAYROLL_PERIOD': 'Payroll',
  'fr.monthly.kind.SALARY_EXPENSES': 'Salary expenses',
  'fr.monthly.kind.EXPENSES': 'Expenses',

  // Reconciliation
  'fr.rec.subtitle': 'The checks behind the reports. A failed one is shown here and in the report it concerns — never balanced away.',
  'fr.rec.overall': 'Overall',
  'fr.rec.status.PASS': 'Passed',
  'fr.rec.status.WARN': 'Look at it',
  'fr.rec.status.FAIL': 'Failed',
  'fr.rec.status.UNAVAILABLE': 'Unavailable',
  'fr.rec.status.RESTRICTED': 'Needs access',
  'fr.rec.CASH_ROLLFORWARD': 'Opening cash + movements since the cutover = the books’ cash',
  'fr.rec.PARTNER_LOANS_ROLLFORWARD': 'Each partner loan: opening + borrowed − repaid ± corrections = closing',
  'fr.rec.PARTNER_ADVANCES_ROLLFORWARD': 'Each partner’s advances: opening + paid − returned ± corrections = closing',
  'fr.rec.HISTORICAL_ONCE': 'Every finalized historical month counted once, and still apart from the live records',
  'fr.rec.CURRENT_EXPENSES_ONCE': 'Every payroll and expense counted once',
  'fr.rec.NO_FINANCING_IN_OPERATIONS': 'No partner loans, capital, advances or distributions in revenue or expenses',
  'fr.rec.BALANCE_SHEET_EQUATION': 'Assets = liabilities + equity, in every currency',
  'fr.rec.HISTORICAL_NO_CASH': 'Historical months move no cash after the cutover',
  'fr.rec.CURRENCY_CONSISTENCY': 'Currencies: original amounts kept, base values traceable',
  'fr.rec.DOCUMENT_COVERAGE': 'Historical months: what is provided, filed, drafted and corrected',
  'fr.rec.byConstruction': 'Holds by how the reports are built: partner transactions and historical months are never read as revenue, expenses or cash they are not.',
  'fr.rec.reachingCutover': 'Historical months that reach the cutover: {n}.',
  'fr.rec.duplicatePayroll': 'Payroll periods with more than one live expense: {n}.',
  'fr.rec.payrollFromPayroll': 'Paid payrolls counted from the payroll itself, their expense cancelled: {n}.',
  'fr.rec.handSalaries': 'Salary expenses entered by hand in months payroll already paid — check they are not the same salaries: {n}.',
  'fr.rec.unconverted': 'No rate today: {currencies}.',
  'fr.rec.todayRate': 'Valued at today’s rate: {currencies}.',
  'fr.rec.olderBase': 'Valued against an older base currency: {n}.',
  'fr.rec.notProvided': 'Not provided: {months}.',
  'fr.rec.missingFiles': 'Finalized with no file: {months}.',
  'fr.rec.drafts': 'Drafts not counted: {months}.',
  'fr.rec.corrections': 'Corrections on record: {corrections}. Voided months: {voided}.',
  'fr.rec.noOpening': 'There is no opening position: the roll-forward covers what was recorded.',
  'fr.rec.partnerLine': '{name} ({currency}): {opening} → {closing}',

  // Drilldown
  'fr.drill.title': 'Behind the figure',
  'fr.drill.lineTotal': 'The report’s figure',
  'fr.drill.rowsTotal': 'These rows',
  'fr.drill.agrees': 'The rows add up to the figure.',
  'fr.drill.hidden': 'Some rows are hidden: your role does not read {sources}.',
  'fr.drill.back': 'Back',
  'fr.drill.date': 'Date',
  'fr.drill.reference': 'Reference',
  'fr.drill.description': 'Description',
  'fr.drill.source': 'From',
  'fr.drill.inBase': 'In {currency}',
  'fr.drill.source.Payment': 'Payment',
  'fr.drill.source.Expense': 'Expense',
  'fr.drill.source.PayrollPeriod': 'Payroll',
  'fr.drill.source.WalletCashReward': 'Points cash',
  'fr.drill.source.HistoricalFinancialRecord': 'Historical month',
  'fr.drill.source.PartnerTransaction': 'Partner transaction',
  'fr.drill.line.CUSTOMER_PAYMENTS': 'Customer payments received',
  'fr.drill.line.REFUNDS': 'Refunds paid',
  'fr.drill.line.SALARY_EXPENSES': 'Salary expenses',
  'fr.drill.line.PAYROLL_WITHOUT_EXPENSE': 'Paid payroll with no expense of its own',
  'fr.drill.line.POINTS_CASH': 'Points cash paid by hand',
  'fr.drill.line.OTHER_EXPENSES': 'Other expenses',
  'fr.drill.line.HISTORICAL_SALARIES': 'Historical salaries',
  'fr.drill.line.HISTORICAL_EXPENSES': 'Historical other expenses',
} as const;

export const reportsAr: Record<keyof typeof reportsEn, string> = {
  'nav.groupReports': 'التقارير المالية',
  'nav.frOverview': 'النظرة المالية العامة',
  'nav.frPnl': 'قائمة الدخل',
  'nav.frBalanceSheet': 'الميزانية العمومية',
  'nav.frCashFlow': 'التدفقات النقدية',
  'nav.frPartners': 'كشف حساب الشركاء',
  'nav.frMonthly': 'الملخص الشهري',
  'nav.frReconciliation': 'المطابقة',

  'fr.period': 'الفترة',
  'fr.period.thisMonth': 'هذا الشهر',
  'fr.period.lastMonth': 'الشهر الماضي',
  'fr.period.ytd': 'منذ بداية السنة',
  'fr.period.thisYear': 'هذه السنة',
  'fr.period.lastYear': 'السنة الماضية',
  'fr.period.custom': 'فترة مخصصة',
  'fr.from': 'من',
  'fr.to': 'إلى',
  'fr.asOf': 'في تاريخ',
  'fr.export': 'تصدير CSV',
  'fr.exported': 'جاري تنزيل الملف.',

  'fr.inBase': 'بعملة {currency}',
  'fr.generated': 'تم الحساب في {time}',
  'fr.cutoverOn': 'تاريخ الانتقال المحاسبي {date}',
  'fr.cutoverDraft': 'تاريخ الانتقال المحاسبي {date} (ما زال مسودة)',
  'fr.cutoverNone': 'لم يتم تحديد تاريخ الانتقال المحاسبي بعد',
  'fr.basis': 'الإيرادات عند التحصيل · المصروفات عند الاعتماد · كل كشف رواتب مدفوع مرة واحدة · الأشهر التاريخية في آخر يوم منها',
  'fr.calendarYear': 'سنوات ميلادية',
  'fr.readOnly': 'التقارير للقراءة فقط: لا شيء هنا يسجّل مالًا أو يغيّره أو يعكسه.',
  'fr.status.VERIFIED': 'موثّق',
  'fr.status.PREVIEW': 'معاينة، لم تتم مطابقتها بعد',
  'fr.status.UNVERIFIED': 'مسجّل، غير موثّق',
  'fr.status.PARTIAL': 'جزئي',
  'fr.status.NOT_RECONCILED': 'غير مطابق',
  'fr.status.SETUP_NEEDED': 'يحتاج إلى إعداد',
  'fr.status.UNAVAILABLE': 'غير متاح',
  'fr.status.RESTRICTED': 'يحتاج إلى صلاحية',
  'fr.statusHint.VERIFIED': 'مبني على مركز افتتاحي معتمد، وكل فحوصه ناجحة.',
  'fr.statusHint.PREVIEW': 'تاريخ الانتقال ما زال مسودة، وقد يتغيّر المركز الافتتاحي.',
  'fr.statusHint.UNVERIFIED': 'لا يوجد مركز افتتاحي، لذلك نحسب ما تم تسجيله فقط.',
  'fr.statusHint.PARTIAL': 'جزء من الفترة قبل تاريخ الانتقال، أو لم يتم إدخال بعض بياناتها.',
  'fr.statusHint.NOT_RECONCILED': 'فشل أحد الفحوص. نعرض الفرق كما هو ولا نخفيه أبدًا.',
  'fr.statusHint.SETUP_NEEDED': 'حدّد تاريخ الانتقال المحاسبي والمركز الافتتاحي في «الإعداد المالي» أولًا.',
  'fr.statusHint.UNAVAILABLE': 'هذا اليوم قبل تاريخ الانتقال المحاسبي، فلا توجد عنه بيانات موثوقة.',
  'fr.statusHint.RESTRICTED': 'صلاحيات دورك لا تشمله.',
  'fr.setupLink': 'افتح الإعداد المالي',

  'fr.notice.NO_CONFIRMED_CUTOVER': 'لا يوجد تاريخ انتقال محاسبي معتمد، لذلك لا نستطيع التأكد من اكتمال أي شهر.',
  'fr.notice.PERIOD_BEFORE_CUTOVER': 'جزء من هذه الفترة قبل تاريخ الانتقال المحاسبي ({cutoverDate})، وقد تكون الإيرادات والتكاليف الأخرى في ذلك الجزء ناقصة.',
  'fr.notice.HISTORICAL_RESTRICTED': 'صلاحيات دورك لا تشمل الأشهر التاريخية، لذلك لا تدخل تكاليفها في هذه الإجماليات.',
  'fr.notice.MONTHS_NOT_PROVIDED': 'لم يتم إدخال أي بيانات لبعض الأشهر، فهي غير معروفة وليست صفرًا.',
  'fr.notice.OVERLAP_CHANGED': 'تغيّرت سجلات النظام لشهر تاريخي بعد مراجعته، فقد يحسب التقرير بعض التكاليف مرتين.',
  'fr.notice.UNCONVERTED': 'لا يوجد سعر صرف اليوم للمبالغ بعملة {currencies}، لذلك لم ندخلها في الإجماليات بالعملة الأساسية، ولم نحسبها صفرًا.',
  'fr.notice.EXPENSES_AT_TODAY_RATE': 'المصروفات بعملة أخرى ليس لها سعر صرف خاص بها، لذلك نقيّمها بسعر اليوم من «الإعدادات › العملات».',
  'fr.notice.REVENUE_INCLUDES_TAX': 'فواتير مدفوعة عليها ضريبة: {invoices}. الإيرادات هي ما تم تحصيله شاملًا الضريبة، لأن النظام لا يحتفظ بحساب للضريبة.',
  'fr.notice.PARTIAL_MONTH': 'تنتهي الفترة قبل نهاية الشهر. والإجماليات الشهرية، مثل الشهر التاريخي أو كشف الرواتب الذي ليس له مصروف، نحسبها في آخر يوم من الشهر، لذلك لا تظهر هنا.',
  'fr.notice.NO_CUTOVER': 'حدّد تاريخ الانتقال المحاسبي والمركز الافتتاحي في «الإعداد المالي» لتظهر الأرصدة.',
  'fr.notice.BEFORE_CUTOVER': 'قبل تاريخ الانتقال المحاسبي ({cutoverDate}) لا توجد في الدفاتر أرصدة موثّقة.',
  'fr.notice.CUTOVER_DRAFT': 'تاريخ الانتقال المحاسبي ما زال مسودة، لذلك هذه معاينة.',
  'fr.notice.NOT_BALANCED': 'الأصول لا تساوي مجموع الالتزامات وحقوق الملكية. الفرق ظاهر أدناه.',
  'fr.notice.HISTORICAL_REACHES_CUTOVER': 'يوجد شهر تاريخي يصل إلى تاريخ الانتقال: تكلفته في قائمة الدخل، لكن أمواله ليست في هذه الدفاتر.',
  'fr.notice.PER_CURRENCY': 'كل عملة منفصلة عن غيرها، فلا نجمع أبدًا مبالغ بعملتين مختلفتين.',
  'fr.notice.STARTS_AT_CUTOVER': 'تبدأ القائمة من تاريخ الانتقال المحاسبي ({cutoverDate})، لأن الدفاتر لا تحتوي على حركة الأموال قبله.',
  'fr.notice.ENDS_TODAY': 'تنتهي اليوم ({today}).',
  'fr.notice.CASH_NOT_RECONCILED': 'القائمة لا تطابق إجمالي الدفاتر، والفرق ظاهر.',
  'fr.notice.NO_BANK_STATEMENTS': 'لا يحتفظ النظام بكشوف حسابات البنك، لذلك قارن النقدية في النهاية بكشف البنك يدويًا.',
  'fr.notice.NO_OFFSET': 'قرض الشريك وسلفه رصيدان منفصلان. الصافي للعلم فقط، ولا تتم أي مقاصّة دون تسوية معتمدة.',
  'fr.notice.OWNERSHIP_NOT_PROFIT': 'حصص الملكية وحدها لا توزّع أي أرباح. الأرباح تحتاج إلى توزيع مسجّل.',
  'fr.notice.ROWS_HIDDEN': 'بعض الصفوف مخفية، لأن صلاحيات دورك لا تشملها.',

  'fr.loss': 'خسارة',
  'fr.profit': 'ربح',
  'fr.leftOut': 'لا يشمل {amounts} بلا سعر صرف',
  'fr.atTodayRate': 'يشمل مبالغ مقيّمة بسعر اليوم',
  'fr.notProvided': 'لم يتم إدخاله',
  'fr.noRecords': 'لا سجلات',
  'fr.open': 'فتح',
  'fr.total': 'الإجمالي',
  'fr.month': 'الشهر',
  'fr.currency': 'العملة',
  'fr.amount': 'المبلغ',
  'fr.byCurrency': 'بالعملات التي تم التسجيل بها',

  'fr.card.revenue': 'إجمالي الإيرادات',
  'fr.card.salaries': 'إجمالي الرواتب',
  'fr.card.otherExpenses': 'مصروفات تشغيلية أخرى',
  'fr.card.operatingExpenses': 'إجمالي المصروفات التشغيلية',
  'fr.card.netProfit': 'صافي الربح / الخسارة',
  'fr.card.cash': 'رصيد النقدية والبنوك',
  'fr.card.partnerLoans': 'قروض الشركاء المستحقة',
  'fr.card.partnerAdvances': 'سلف الشركاء المستحقة للشركة',
  'fr.card.asOf': 'في {date}',
  'fr.card.period': '{from} – {to}',
  'fr.card.none': 'لا شيء',
  'fr.chart.revenueVsExpenses': 'الإيرادات والمصروفات التشغيلية',
  'fr.chart.profitTrend': 'الربح أو الخسارة شهرًا بشهر',
  'fr.chart.breakdown': 'المصروفات التشغيلية حسب مصدرها',
  'fr.chart.cashMonthly': 'النقدية الداخلة والخارجة منذ تاريخ الانتقال',
  'fr.chart.partners': 'قروض الشركاء وسلفهم',
  'fr.series.revenue': 'الإيرادات',
  'fr.series.expenses': 'المصروفات التشغيلية',
  'fr.series.profit': 'الربح / الخسارة',
  'fr.series.inflow': 'وارد',
  'fr.series.outflow': 'صادر',
  'fr.series.loans': 'قروض مستحقة',
  'fr.series.advances': 'سلف مستحقة',
  'fr.series.salariesCurrent': 'الرواتب الحالية',
  'fr.series.salariesHistorical': 'الرواتب التاريخية',
  'fr.series.otherCurrent': 'المصروفات الأخرى الحالية',
  'fr.series.otherHistorical': 'المصروفات الأخرى التاريخية',
  'fr.coverageSummary': 'مصادر بيانات هذه الأشهر',
  'fr.coverageSummary.notProvided': 'أشهر فيها بيانات لم يتم إدخالها: {n}',
  'fr.coverageSummary.overlapChanged': 'أشهر غير مطابقة: {n}',
  'fr.coverageSummary.missingDocuments': 'سجلات تاريخية بلا ملف: {n}',
  'fr.coverageSummary.drafts': 'مسودات تاريخية غير محسوبة: {n}',
  'fr.coverageSummary.allClear': 'كل الأشهر مغطّاة.',
  'fr.cashChartNone': 'لا توجد بيانات قبل تاريخ الانتقال المحاسبي.',

  'fr.coverage.LIVE': 'مسجّل',
  'fr.coverage.HISTORICAL': 'ملخص تاريخي',
  'fr.coverage.HISTORICAL_AND_LIVE': 'تاريخي + مسجّل',
  'fr.coverage.OVERLAP_CHANGED': 'غير مطابق',
  'fr.coverage.NO_RECORDS': 'لا سجلات',
  'fr.coverage.NOT_PROVIDED': 'لم يتم إدخاله',
  'fr.coverage.RECORDED': 'مسجّل',
  'fr.coverage.profit.COMPLETE': 'مكتمل',
  'fr.coverage.profit.PARTIAL': 'جزئي',
  'fr.coverage.profit.NOT_RECONCILED': 'غير مطابق',
  'fr.coverage.partialMonth': 'جزء من الشهر',
  'fr.coverage.beforeCutover': 'قبل تاريخ الانتقال المحاسبي',
  'fr.coverage.files': 'الملفات: {n}',
  'fr.coverage.noFile': 'بلا ملف',
  'fr.coverage.drafts': 'مسودات غير محسوبة: {n}',
  'fr.coverage.salaries': 'الرواتب',
  'fr.coverage.other': 'مصروفات أخرى',
  'fr.coverage.revenue': 'الإيرادات',

  'fr.pnl.subtitle': 'ما كسبته الشركة وما كلّفها ذلك خلال فترة: الإيرادات عند تحصيلها، وكل تكلفة مرة واحدة.',
  'fr.pnl.revenue': 'الإيرادات',
  'fr.pnl.customerPayments': 'مدفوعات العملاء المحصّلة',
  'fr.pnl.refunds': 'المبالغ المردودة',
  'fr.pnl.netRevenue': 'صافي الإيرادات',
  'fr.pnl.operatingExpenses': 'المصروفات التشغيلية',
  'fr.pnl.salariesCurrent': 'الرواتب الحالية',
  'fr.pnl.salariesHistorical': 'الرواتب من الملخص التاريخي',
  'fr.pnl.otherCurrent': 'المصروفات الأخرى الحالية',
  'fr.pnl.otherHistorical': 'المصروفات الأخرى من الملخص التاريخي',
  'fr.pnl.totalExpenses': 'إجمالي المصروفات التشغيلية',
  'fr.pnl.operatingProfit': 'الربح / الخسارة التشغيلية',
  'fr.pnl.nonOperating': 'إيرادات ومصروفات أخرى',
  'fr.pnl.taxes': 'ضريبة الدخل',
  'fr.pnl.notRecorded': 'لا يسجّل النظام شيئًا منها',
  'fr.pnl.netProfit': 'صافي الربح / الخسارة',
  'fr.pnl.line.SALARY_EXPENSES': 'مصروفات الرواتب، ومنها مصروفات كشوف الرواتب',
  'fr.pnl.line.PAYROLL_WITHOUT_EXPENSE': 'كشف رواتب مدفوع ليس له مصروف خاص',
  'fr.pnl.line.POINTS_CASH': 'مكافآت النقاط المدفوعة يدويًا',
  'fr.pnl.uncategorized': 'بلا فئة',
  'fr.pnl.showMonths': 'شهرًا بشهر',
  'fr.pnl.drill': 'افتح تفاصيل الرقم',
  'fr.pnl.coverageRow': 'مصدر البيانات',
  'fr.pnl.noFinancing': 'قروض الشركاء ورأس المال والسلف والتوزيعات ليست إيرادات ولا مصروفات أبدًا، ومكانها الميزانية العمومية والتدفقات النقدية وكشف حساب الشركاء.',

  'fr.bs.subtitle': 'ما تملكه الشركة، وما عليها، وصافي قيمتها في يوم محدد، لكل عملة على حدة.',
  'fr.bs.assets': 'الأصول',
  'fr.bs.cash': 'النقدية والبنوك',
  'fr.bs.advances': 'سلف الشركاء المستحقة للشركة',
  'fr.bs.prepaid': 'مصروفات مدفوعة مقدمًا',
  'fr.bs.totalAssets': 'إجمالي الأصول',
  'fr.bs.liabilities': 'الالتزامات',
  'fr.bs.loans': 'قروض الشركاء المستحقة',
  'fr.bs.unpaid': 'مصروفات معتمدة غير مدفوعة',
  'fr.bs.totalLiabilities': 'إجمالي الالتزامات',
  'fr.bs.equity': 'حقوق الملكية',
  'fr.bs.capital': 'رأس مال الشركاء',
  'fr.bs.openingEquity': 'حقوق الملكية الافتتاحية الزائدة على رأس مال الشركاء (محسوبة عند تاريخ الانتقال)',
  'fr.bs.netIncome': 'صافي الدخل منذ تاريخ الانتقال',
  'fr.bs.distributions': 'توزيعات الأرباح',
  'fr.bs.adjustments': 'الطرف المقابل لتصحيحات أرصدة الشركاء',
  'fr.bs.totalEquity': 'إجمالي حقوق الملكية',
  'fr.bs.check': 'الأصول = الالتزامات + حقوق الملكية',
  'fr.bs.balanced': 'متوازنة',
  'fr.bs.difference': 'الفرق: {amount}',
  'fr.bs.derivation': 'طريقة حساب حقوق الملكية الافتتاحية',
  'fr.bs.derivationText': 'صباح يوم الانتقال: {cash} نقدية + {advances} سلف + {prepaid} مدفوع مقدمًا − {loans} قروض شركاء − {unpaid} مصروفات غير مدفوعة − {capital} رأس مال الشركاء.',
  'fr.bs.noReceivables': 'لا توجد ذمم مدينة: نحسب الإيرادات عند تحصيلها، لذلك الفاتورة غير المدفوعة ليست أصلًا في هذه الدفاتر. الفواتير المستحقة موجودة في «التقارير › المستحقات».',
  'fr.bs.historical': 'أشهر تاريخية تصل إلى تاريخ الانتقال',
  'fr.bs.historicalHint': 'تكلفتها في قائمة الدخل، لكن أموالها ليست في هذه الدفاتر.',

  'fr.cf.subtitle': 'أموال الشركة الداخلة والخارجة منذ تاريخ الانتقال المحاسبي، لكل عملة على حدة.',
  'fr.cf.beginning': 'النقدية في البداية',
  'fr.cf.operating': 'الأنشطة التشغيلية',
  'fr.cf.investing': 'الأنشطة الاستثمارية',
  'fr.cf.investingNone': 'لا يتتبعها النظام',
  'fr.cf.financing': 'الأنشطة التمويلية',
  'fr.cf.net': 'الصافي',
  'fr.cf.netChange': 'صافي التغيّر',
  'fr.cf.ending': 'النقدية في النهاية',
  'fr.cf.books': 'إجمالي الدفاتر',
  'fr.cf.reconciled': 'مطابقة للدفاتر',
  'fr.cf.difference': 'الفرق: {amount}',
  'fr.cf.transfers': 'تحويلات بين حسابات الشركة، مستبعدة من الإجمالي: {amount}',
  'fr.cf.accounts': 'حسب الحساب',
  'fr.cf.account': 'الحساب',
  'fr.cf.unassigned': 'غير مرتبط بحساب',
  'fr.cf.unassignedHint': 'مدفوعات العملاء والمصروفات والرواتب ومكافآت النقاط لا تحدد أي حساب في أقسامها.',
  'fr.cf.in': 'وارد',
  'fr.cf.out': 'صادر',
  'fr.cf.line.CUSTOMER_RECEIPTS': 'مدفوعات العملاء المحصّلة',
  'fr.cf.line.REFUNDS_PAID': 'مبالغ مردودة',
  'fr.cf.line.SALARIES_PAID': 'رواتب مدفوعة',
  'fr.cf.line.POINTS_CASH_PAID': 'مكافآت النقاط المدفوعة يدويًا',
  'fr.cf.line.OTHER_EXPENSES_PAID': 'مصروفات أخرى مدفوعة',
  'fr.cf.line.PARTNER_LOANS_RECEIVED': 'قروض من الشركاء',
  'fr.cf.line.PARTNER_LOANS_REPAID': 'قروض مسددة للشركاء',
  'fr.cf.line.CAPITAL_CONTRIBUTED': 'رأس مال دفعه الشركاء',
  'fr.cf.line.DISTRIBUTIONS_PAID': 'أرباح موزعة على الشركاء',
  'fr.cf.line.ADVANCES_PAID': 'سلف مدفوعة للشركاء',
  'fr.cf.line.ADVANCES_REPAID': 'سلف ردّها الشركاء',

  'fr.partners.subtitle': 'ما تدين به الشركة لكل شريك، وما يدين به كل شريك للشركة، في بداية الفترة ونهايتها.',
  'fr.partners.share': 'الحصة',
  'fr.partners.since': 'منذ {date}',
  'fr.partners.noShare': 'لا حصة مسجّلة',
  'fr.partners.loan': 'القرض المستحق',
  'fr.partners.advances': 'السلف المستحقة',
  'fr.partners.capital': 'رأس المال',
  'fr.partners.distributions': 'التوزيعات',
  'fr.partners.net': 'الصافي، للعلم فقط',
  'fr.partners.beginning': 'في البداية',
  'fr.partners.closing': 'في النهاية',
  'fr.partners.movements': 'خلال الفترة',
  'fr.partners.mv.loanReceived': 'أقرض',
  'fr.partners.mv.loanRepaid': 'سدّدت الشركة',
  'fr.partners.mv.expensesPaidAsLoan': 'مصروفات دفعها كقرض',
  'fr.partners.mv.loanAdjustments': 'تصحيحات',
  'fr.partners.mv.advancesPaid': 'سلّفته الشركة',
  'fr.partners.mv.advancesReturned': 'ردّ',
  'fr.partners.mv.advanceAdjustments': 'تصحيحات',
  'fr.partners.mv.capitalContributed': 'دفع',
  'fr.partners.mv.expensesPaidAsCapital': 'مصروفات دفعها كرأس مال',
  'fr.partners.mv.capitalAdjustments': 'تصحيحات',
  'fr.partners.mv.distributions': 'وزّعت عليه الشركة',
  'fr.partners.reconciles': 'البداية + الحركة = النهاية',
  'fr.partners.notReconciled': 'البداية + الحركة ≠ النهاية',
  'fr.partners.totals': 'كل الشركاء',
  'fr.partners.none': 'لا يوجد شركاء بعد.',
  'fr.partners.statement': 'كشف الحساب',
  'fr.partners.search': 'ابحث في كشف الحساب',
  'fr.partners.history': 'سجل تاريخي قبل تاريخ الانتقال',
  'fr.partners.reversed': 'معكوسة',
  'fr.partners.noCash': 'لا حركة نقدية',
  'fr.partners.after': 'الأرصدة بعدها',
  'fr.partners.shareHistory': 'سجل الملكية',
  'fr.partners.until': 'حتى {date}',
  'fr.partners.expensePaid': 'دفع المصروف «{description}»',
  'fr.partners.noRows': 'لا شيء في هذه الفترة.',
  'fr.partners.openRecord': 'افتح المعاملة',

  'fr.monthly.subtitle': 'صف لكل شهر: الإيرادات والرواتب والمصروفات الأخرى من أقسام النظام ومن الملخصات التاريخية، ثم الربح، ومصدر بيانات كل رقم.',
  'fr.monthly.revenue': 'الإيرادات',
  'fr.monthly.histSalaries': 'رواتب تاريخية',
  'fr.monthly.curSalaries': 'رواتب حالية',
  'fr.monthly.histOther': 'مصروفات أخرى تاريخية',
  'fr.monthly.curOther': 'مصروفات أخرى حالية',
  'fr.monthly.totalExpenses': 'إجمالي المصروفات',
  'fr.monthly.profit': 'الربح / الخسارة',
  'fr.monthly.files': 'الملفات',
  'fr.monthly.coverage': 'التغطية',
  'fr.monthly.year': 'سنة {year}',
  'fr.monthly.overlaps': 'أشهر تاريخية تغيّرت سجلات النظام الخاصة بها بعد مراجعتها',
  'fr.monthly.overlapLine': '{kind} {currency}: عند المراجعة {reviewed}، والآن {now}',
  'fr.monthly.sources': 'المطابقة ومصادر البيانات',
  'fr.monthly.sourceLive': 'الحالي: من أقسام النظام، وهي المدفوعات والمصروفات المعتمدة والرواتب المدفوعة ومكافآت النقاط المدفوعة يدويًا.',
  'fr.monthly.sourceHistorical': 'التاريخي: ملخصات شهرية معتمدة، لكل منها ملف Excel أو PDF. ولا نحسب المسودة أبدًا.',
  'fr.monthly.sourceOverlap': 'إذا كان للشهر الاثنان معًا، فقد تمت مراجعة الإجمالي التاريخي للتأكد من أنه لا يشمل سجلات النظام (مراجعة التداخل في المرحلة الأولى).',
  'fr.monthly.kind.PAYROLL_PERIOD': 'كشف الرواتب',
  'fr.monthly.kind.SALARY_EXPENSES': 'مصروفات الرواتب',
  'fr.monthly.kind.EXPENSES': 'المصروفات',

  'fr.rec.subtitle': 'الفحوص التي تقوم عليها التقارير. أي فحص يفشل يظهر هنا وفي التقرير الخاص به، ولا نعدّل الأرقام أبدًا لإخفاء الفرق.',
  'fr.rec.overall': 'النتيجة العامة',
  'fr.rec.status.PASS': 'ناجح',
  'fr.rec.status.WARN': 'للمراجعة',
  'fr.rec.status.FAIL': 'فاشل',
  'fr.rec.status.UNAVAILABLE': 'غير متاح',
  'fr.rec.status.RESTRICTED': 'يحتاج إلى صلاحية',
  'fr.rec.CASH_ROLLFORWARD': 'النقدية الافتتاحية + الحركات منذ تاريخ الانتقال = النقدية في الدفاتر',
  'fr.rec.PARTNER_LOANS_ROLLFORWARD': 'قرض كل شريك: الافتتاحي + المقترض − المسدد ± التصحيحات = الختامي',
  'fr.rec.PARTNER_ADVANCES_ROLLFORWARD': 'سلف كل شريك: الافتتاحي + المدفوع − المردود ± التصحيحات = الختامي',
  'fr.rec.HISTORICAL_ONCE': 'كل شهر تاريخي معتمد محسوب مرة واحدة، ومنفصل عن سجلات النظام',
  'fr.rec.CURRENT_EXPENSES_ONCE': 'كل كشف رواتب وكل مصروف محسوب مرة واحدة',
  'fr.rec.NO_FINANCING_IN_OPERATIONS': 'لا قروض شركاء ولا رأس مال ولا سلف ولا توزيعات في الإيرادات أو المصروفات',
  'fr.rec.BALANCE_SHEET_EQUATION': 'الأصول = الالتزامات + حقوق الملكية، في كل عملة',
  'fr.rec.HISTORICAL_NO_CASH': 'الأشهر التاريخية لا تحرّك نقدية بعد تاريخ الانتقال',
  'fr.rec.CURRENCY_CONSISTENCY': 'العملات: المبالغ الأصلية محفوظة، والقيم بالعملة الأساسية قابلة للتتبع',
  'fr.rec.DOCUMENT_COVERAGE': 'الأشهر التاريخية: ما تم إدخاله، وما له ملف، وما بقي مسودة، وما تم تصحيحه',
  'fr.rec.byConstruction': 'مضمون بطريقة بناء التقارير: لا تعامل التقارير معاملات الشركاء ولا الأشهر التاريخية على أنها إيرادات أو مصروفات أو نقدية وهي ليست كذلك.',
  'fr.rec.reachingCutover': 'أشهر تاريخية تصل إلى تاريخ الانتقال: {n}.',
  'fr.rec.duplicatePayroll': 'كشوف رواتب لها أكثر من مصروف غير ملغى: {n}.',
  'fr.rec.payrollFromPayroll': 'كشوف رواتب مدفوعة نحسبها من الكشف نفسه لأن مصروفها ملغى: {n}.',
  'fr.rec.handSalaries': 'مصروفات رواتب مسجّلة يدويًا في أشهر لها كشف رواتب مدفوع، فتأكّد أنها ليست الرواتب نفسها: {n}.',
  'fr.rec.unconverted': 'عملات بلا سعر صرف اليوم: {currencies}.',
  'fr.rec.todayRate': 'عملات مقيّمة بسعر اليوم: {currencies}.',
  'fr.rec.olderBase': 'سجلات مقيّمة بعملة أساسية سابقة: {n}.',
  'fr.rec.notProvided': 'أشهر لم يتم إدخالها: {months}.',
  'fr.rec.missingFiles': 'أشهر معتمدة بلا ملف: {months}.',
  'fr.rec.drafts': 'مسودات غير محسوبة: {months}.',
  'fr.rec.corrections': 'التصحيحات المسجّلة: {corrections}. الأشهر الملغاة: {voided}.',
  'fr.rec.noOpening': 'لا يوجد مركز افتتاحي، لذلك تشمل حركة الأرصدة ما تم تسجيله فقط.',
  'fr.rec.partnerLine': '{name} ({currency}): {opening} ← {closing}',

  'fr.drill.title': 'تفاصيل الرقم',
  'fr.drill.lineTotal': 'الرقم في التقرير',
  'fr.drill.rowsTotal': 'مجموع هذه الصفوف',
  'fr.drill.agrees': 'مجموع الصفوف يساوي الرقم.',
  'fr.drill.hidden': 'بعض الصفوف مخفية، لأن صلاحيات دورك لا تشمل {sources}.',
  'fr.drill.back': 'رجوع',
  'fr.drill.date': 'التاريخ',
  'fr.drill.reference': 'المرجع',
  'fr.drill.description': 'الوصف',
  'fr.drill.source': 'المصدر',
  'fr.drill.inBase': 'بعملة {currency}',
  'fr.drill.source.Payment': 'دفعة',
  'fr.drill.source.Expense': 'مصروف',
  'fr.drill.source.PayrollPeriod': 'كشف رواتب',
  'fr.drill.source.WalletCashReward': 'مكافأة نقاط',
  'fr.drill.source.HistoricalFinancialRecord': 'شهر تاريخي',
  'fr.drill.source.PartnerTransaction': 'معاملة شريك',
  'fr.drill.line.CUSTOMER_PAYMENTS': 'مدفوعات العملاء المحصّلة',
  'fr.drill.line.REFUNDS': 'مبالغ مردودة',
  'fr.drill.line.SALARY_EXPENSES': 'مصروفات الرواتب',
  'fr.drill.line.PAYROLL_WITHOUT_EXPENSE': 'كشف رواتب مدفوع ليس له مصروف خاص',
  'fr.drill.line.POINTS_CASH': 'مكافآت النقاط المدفوعة يدويًا',
  'fr.drill.line.OTHER_EXPENSES': 'مصروفات أخرى',
  'fr.drill.line.HISTORICAL_SALARIES': 'رواتب تاريخية',
  'fr.drill.line.HISTORICAL_EXPENSES': 'مصروفات أخرى تاريخية',
};
